import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getSidebarForRole } from "../config/sidebarConfig";
import { FiMenu } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import UserContext from "../context/UserContext";
import { isFeatureAllowed } from "../utils/FeatureAccess";
import { FaCrown } from "react-icons/fa";

function Sidebar() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isCollapsed, setIsCollapsed] = useState(
    JSON.parse(localStorage.getItem("isCollapsed")) ?? true
  );
  const { user, packageType } = useContext(UserContext);

  const userRole = user?.role === "reception" ? "receptionist" : user?.role;
  const { t, i18n } = useTranslation();
  const isActive = (path) => {
    const currentPath = window.location.pathname;
    return path === `/${userRole}`
      ? currentPath === `/${userRole}`
      : currentPath.startsWith(path);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (isMobile) {
        setIsCollapsed(false); // Automatically collapse on mobile
      }
    };

    // Run on initial load and on window resize
    handleResize();
    window.addEventListener("resize", handleResize);

    // Cleanup on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  useEffect(() => {
    localStorage.setItem("isCollapsed", JSON.stringify(isCollapsed)); // Save state to localStorage
  }, [isCollapsed]);

  if (!user?.role) {
    return null;
  }

  const sidebarMenu = getSidebarForRole(user?.role);
  // const sidebarMenu = getSidebarForRole(user?.role).filter(
  //   (item) => !item.feature || isFeatureAllowed(packageType, item.feature)
  // );
  return (
    <div
      className={`flex flex-col py-6 h-screen border-r border-border ${
        !isMobile && "transitions-all duration-300"
      } ${isCollapsed ? "w-[80px]" : "w-[250px]"}`}>
      {/* Logo Section */}
      <div
        className={`flex items-center ${
          isCollapsed ? "justify-center" : "justify-start"
        } gap-3 px-4 mb-6`}>
        <Link to={`/${userRole}`}>
          {isCollapsed ? (
            <img
              src={"/images/iterum-logo.png"}
              alt="logo"
              className={`object-contain w-10`}
            />
          ) : (
            <img
              src={"/images/iterum-logo-writting.png"}
              alt="logo"
              className={`object-contain w-3/4 ml-5`}
            />
          )}
        </Link>
        {!isCollapsed && !isMobile && (
          <button
            onClick={() => setIsCollapsed(true)}
            className="ml-auto text-gray-600 hover:text-subMain">
            <FiMenu className="text-2xl" />
          </button>
        )}
      </div>

      {/* Toggle Button */}
      <div className="flex justify-center mb-4">
        {isCollapsed && !isMobile && (
          <button
            onClick={() => setIsCollapsed(false)}
            className="text-gray-600 hover:text-subMain">
            <FiMenu className="text-2xl" />
          </button>
        )}
      </div>

      {/* Sidebar Menu */}
      {/* Sidebar Menu */}
      <div className="flex flex-col gap-2">
        {sidebarMenu.map((item, index) => (
          <div className="relative mx-2" key={index}>
            <Link
              to={item.path}
              className={`group flex items-center p-3 rounded-lg ${
                !isMobile && "transitions-all duration-300"
              } ${isCollapsed ? "justify-center" : "justify-between px-6"} ${
                isActive(item.path)
                  ? "bg-subMain text-white shadow-lg"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              style={{
                width: isCollapsed ? "auto" : "calc(100% - 20px)",
                margin: isCollapsed ? "0 auto" : "0 10px",
              }}>
              {/* Icon + Text */}
              <div className="flex items-center gap-4 relative">
                {/* Crown when collapsed */}
                {isCollapsed &&
                  item.feature &&
                  !isFeatureAllowed(packageType, item.feature) && (
                    <span className="absolute -top-2 -right-3">
                      <FaCrown
                        className={`${
                          isActive(item.path) ? "text-white" : "text-[#6CCCF1]"
                        } text-xs`}
                      />
                    </span>
                  )}
                <item.icon
                  className={`text-xl ${
                    isActive(item.path) ? "text-white" : "text-gray-500"
                  }`}
                />
                {!isCollapsed && (
                  <p
                    className={`text-sm font-medium ${
                      isActive(item.path) ? "text-white" : "text-gray-600"
                    }`}>
                    {t(item.title)}
                  </p>
                )}
              </div>

              {/* Pro Badge Expanded */}
              {!isCollapsed &&
                item.feature &&
                !isFeatureAllowed(packageType, item.feature) && (
                  <span
                    className={`${
                      isActive(item.path)
                        ? "bg-white text-[#6CCCF1]"
                        : "bg-[#6CCCF1]/10 text-[#6CCCF1]"
                    } text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1`}>
                    <FaCrown className="text-[12px]" />
                    Pro
                  </span>
                )}

              {/* Hover title when collapsed */}
              {isCollapsed && (
                <span
                  className={`absolute z-[100] ${
                    i18n.language === "ar" ? "right-full" : "left-full"
                  } ml-2 bg-black text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}>
                  {t(item.title)}
                </span>
              )}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Sidebar;
